import { ReactElement, useState } from 'react';
import { DialogTitle, DialogContentText, Stack, Button } from '@mui/material';
import QRCode from 'react-qr-code';

import { useRequest } from '~hooks/useRequest';
import { useClientOnMethodMultiple } from '~hooks/useSignalR';
import { getWhatsAppQR } from '~services/admin/companySetting';
import { METHODS_NAME } from '~constants/signalR';
import { NotificationWhatsappQRUpdated } from '~globals/types/notifications';

import LoadingFallback from '../LoadingFallback';

import { DialogLinkWhatsappOwnLineProps } from './types';
import {
  DialogLinkWhatsappOwnLineContainer,
  DialogLinkWhatsappOwnLineContent,
  DialogLinkWhatsappOwnLineList,
  DialogLinkWhatsappOwnLineListItem,
  DialogLinkWhatsappOwnLineListItemAvatar,
  DialogLinkWhatsappOwnLineAvatar,
  DialogLinkWhatsappOwnLineListItemText,
  DialogLinkWhatsappOwnLineQRContainer,
  DialogLinkWhatsappOwnLineActions,
} from './styles';

const INSTRUCTIONS = [
  <>
    Abre <b>WhatsApp</b> en tu teléfono.
  </>,
  <>
    Ve al <b>menú</b> (tres puntos) o a preferencias (tuerca)
  </>,
  <>
    Selecciona <b>Dispositivos vinculados</b> o WhatsApp Web / Escritorio
  </>,
  <>
    Oprime la opción <b>Vincular un dispositivo</b> y apunta tu cámara al código
    QR en esta pantalla.
  </>,
  <>
    Mantén tu teléfono <b>firme</b> hasta que el escaneo se complete.
  </>,
  <>
    Una vez que se haya escaneado, tu cuenta de WhatsApp será vinculada con
    Routix y <b>podrás comenzar a enviar notificaciones</b> de tu propia línea.
  </>,
];

const DialogLinkWhatsappOwnLine = ({
  open,
  onClose,
}: DialogLinkWhatsappOwnLineProps): ReactElement => {
  const [qrValue, setQrValue] = useState<string>('');

  const [loadingQR, setLoadingQR] = useState(true);

  useRequest({
    request: getWhatsAppQR,
    initialLoading: loadingQR,
    payload: null,
    withPostSuccess: (response) => {
      setLoadingQR(false);

      if (response.data?.data) {
        setQrValue(response.data.data);
      }
    },
    withPostFailure: () => {
      setLoadingQR(false);
    },
  });

  useClientOnMethodMultiple(
    METHODS_NAME.WHATSAPP_EVENTS,
    'DialogLinkWhatsappOwnLine',
    (notification) => {
      const infoNotification = JSON.parse(
        notification,
      ) as NotificationWhatsappQRUpdated;

      if (open && infoNotification?.eventName === 'qrcode.updated') {
        setLoadingQR(true);

        setTimeout(() => {
          setQrValue(infoNotification.eventQR);
          setLoadingQR(false);
        }, 500);
      }
    },
  );

  return (
    <DialogLinkWhatsappOwnLineContainer open={open} maxWidth={false}>
      <DialogTitle>Vincular WhatsApp Línea propia</DialogTitle>

      <DialogLinkWhatsappOwnLineContent dividers={true}>
        <Stack spacing={2}>
          <DialogContentText>
            Para vincular su cuenta de WhatsApp, siga estos pasos:
          </DialogContentText>

          <DialogLinkWhatsappOwnLineList dense>
            {INSTRUCTIONS.map((instruction, instructionIndex) => (
              <DialogLinkWhatsappOwnLineListItem
                key={`dialog-link-whatsapp-own-line-instruction-${instructionIndex}`}
              >
                <DialogLinkWhatsappOwnLineListItemAvatar>
                  <DialogLinkWhatsappOwnLineAvatar>
                    {instructionIndex + 1}
                  </DialogLinkWhatsappOwnLineAvatar>
                </DialogLinkWhatsappOwnLineListItemAvatar>

                <DialogLinkWhatsappOwnLineListItemText>
                  {instruction}
                </DialogLinkWhatsappOwnLineListItemText>
              </DialogLinkWhatsappOwnLineListItem>
            ))}
          </DialogLinkWhatsappOwnLineList>
        </Stack>

        <DialogLinkWhatsappOwnLineQRContainer>
          {loadingQR && <LoadingFallback />}

          {!loadingQR && <QRCode value={qrValue} />}
        </DialogLinkWhatsappOwnLineQRContainer>
      </DialogLinkWhatsappOwnLineContent>

      <DialogLinkWhatsappOwnLineActions>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </DialogLinkWhatsappOwnLineActions>
    </DialogLinkWhatsappOwnLineContainer>
  );
};

export default DialogLinkWhatsappOwnLine;
